import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, XCircle, Send, RefreshCw, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "@/utils/pushNotifications";

export default function AdminPushStatus() {
  const { authAxios, user } = useAuth();
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState({ orders: true, admin_messages: true });
  const [msg, setMsg] = useState("");
  const [when, setWhen] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [browserPerm, setBrowserPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const refresh = async () => {
    setLoading(true);
    try {
      const [res, p] = await Promise.all([
        authAxios.get("/push/status"),
        authAxios.get("/push/preferences"),
      ]);
      setData(res.data);
      setPrefs({ orders: p.data.orders, admin_messages: p.data.admin_messages });
    } catch (e) {
      toast.error("Errore caricamento stato push");
    } finally {
      setLoading(false);
    }
  };

  const savePrefs = async (next) => {
    const prev = prefs;
    setPrefs(next);
    try {
      await authAxios.put("/push/preferences", next);
    } catch {
      setPrefs(prev);
      toast.error("Errore salvataggio preferenze");
    }
  };

  const loadAdminData = async () => {
    try {
      const [r, h] = await Promise.all([
        authAxios.get("/push/recipients"),
        authAxios.get("/push/messages"),
      ]);
      setRecipients(r.data.agents || []);
      setHistory(h.data.items || []);
    } catch { /* non bloccante */ }
  };

  const sendAdminMessage = async (scheduled = false) => {
    if (!msg.trim()) return;
    if (scheduled && !when) { toast.error("Scegli data e ora"); return; }
    setSending(true);
    try {
      const res = await authAxios.post("/push/admin-message", {
        body: msg.trim(),
        user_ids: selected.size ? [...selected] : null,
        scheduled_at: scheduled ? new Date(when).toISOString() : null,
      });
      if (res.data.scheduled) {
        toast.success(`Messaggio programmato per ${new Date(res.data.scheduled_at).toLocaleString("it-IT")}`);
      } else {
        const { sent, failed } = res.data;
        if (sent > 0) toast.success(`Messaggio inviato a ${sent} dispositivo${sent > 1 ? "i" : ""}`);
        else toast.warning(`Nessun agente raggiunto${failed ? ` (${failed} errori)` : ""}`);
      }
      setMsg("");
      setWhen("");
      await loadAdminData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio messaggio");
    } finally { setSending(false); }
  };

  const cancelScheduled = async (id) => {
    try {
      await authAxios.delete(`/push/messages/${id}`);
      toast.success("Messaggio programmato annullato");
      await loadAdminData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore annullamento");
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (user?.role === "admin") loadAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush(authAxios);
      if (res.ok) {
        toast.success("Notifiche attivate su questo dispositivo");
        setBrowserPerm("granted");
        await refresh();
      } else if (res.reason === "denied") {
        toast.error("Permesso negato — abilitalo dalle impostazioni del browser");
        setBrowserPerm("denied");
      } else if (res.reason === "unsupported") {
        toast.message("Browser non supportato");
      } else {
        toast.error(`Errore: ${res.reason}${res.error ? ` — ${res.error}` : ""}`, { duration: 8000 });
      }
    } finally { setBusy(false); }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush(authAxios);
      toast.success("Notifiche disattivate su questo dispositivo");
      await refresh();
    } finally { setBusy(false); }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const res = await authAxios.post("/push/test");
      const { sent, failed, pruned } = res.data;
      if (sent > 0) toast.success(`Push test inviato a ${sent} dispositivo${sent > 1 ? "i" : ""}`);
      else if (pruned > 0) toast.warning(`Subscription scadute rimosse: ${pruned}`);
      else if (failed > 0) toast.error(`Invio fallito su ${failed} dispositivi`);
      else toast.message("Nessun dispositivo iscritto — premi 'Attiva su questo dispositivo'");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio test");
    } finally { setBusy(false); }
  };

  const supported = isPushSupported();

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-push-status-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        <PageHeader title="Diagnostica Notifiche Push" />
        <div className="mb-6">
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-white">Notifiche Push</h1>
          <p className="text-zinc-400 text-xs sm:text-sm">Diagnostica server, dispositivo e invio di test</p>
        </div>

        {/* Server config */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              Configurazione server
              <Button variant="ghost" size="sm" onClick={refresh} disabled={loading} className="text-zinc-400 hover:text-white h-7 px-2 ml-auto">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="VAPID configurato" value={
              data?.vapid_configured
                ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Sì</Badge>
                : <Badge className="bg-red-500/15 text-red-300 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />No</Badge>
            } />
            <Row label="VAPID public key" value={<span className="font-mono text-xs text-zinc-300">{data?.vapid_public_key_prefix || "—"}</span>} />
            <Row label="VAPID subject" value={<span className="font-mono text-xs text-zinc-300">{data?.vapid_subject || "—"}</span>} />
            <Row label="Subscriptions totali" value={<span className="text-white font-mono">{data?.subscriptions_total ?? "—"}</span>} />
            {data?.subscriptions_by_role && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {Object.entries(data.subscriptions_by_role).map(([role, count]) => (
                  <Badge key={role} className="bg-blue-500/15 text-blue-300 border-blue-500/30 text-[10px]" data-testid={`role-count-${role}`}>
                    {role}: {count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* This device */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Questo dispositivo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Browser supportato" value={
              supported
                ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Sì</Badge>
                : <Badge className="bg-red-500/15 text-red-300 border-red-500/30">No (su iOS serve PWA)</Badge>
            } />
            <Row label="Permesso browser" value={
              <Badge className={
                browserPerm === "granted" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : browserPerm === "denied" ? "bg-red-500/15 text-red-300 border-red-500/30"
                : "bg-amber-500/15 text-amber-300 border-amber-500/30"
              }>{browserPerm}</Badge>
            } />
            <Row label="Mie subscription attive" value={<span className="text-white font-mono">{data?.my_subscriptions ?? "—"}</span>} />

            <div className="flex flex-wrap gap-2 pt-3">
              {(browserPerm !== "granted" || (data?.my_subscriptions ?? 0) === 0) && (
                <Button data-testid="enable-push-btn" onClick={handleEnable} disabled={busy || !supported} className="bg-amber-500 hover:bg-amber-600 text-black h-9">
                  <Bell className="w-4 h-4 mr-1.5" /> Attiva su questo dispositivo
                </Button>
              )}
              {browserPerm === "granted" && (data?.my_subscriptions ?? 0) > 0 && (
                <Button data-testid="disable-push-btn" variant="outline" onClick={handleDisable} disabled={busy} className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-9">
                  <BellOff className="w-4 h-4 mr-1.5" /> Disattiva
                </Button>
              )}
              <Button data-testid="test-push-btn" variant="outline" onClick={handleTest} disabled={busy} className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 h-9">
                <Send className="w-4 h-4 mr-1.5" /> Mandami un test
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tipi di notifica */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Quali notifiche ricevere</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <PrefToggle
              testId="pref-orders"
              label="Notifiche ordini"
              hint="Avviso a ogni nuovo ordine inviato"
              on={prefs.orders}
              onToggle={() => savePrefs({ ...prefs, orders: !prefs.orders })}
            />
            <PrefToggle
              testId="pref-admin-messages"
              label="Messaggi dall'admin"
              hint="Comunicazioni inviate dall'amministratore"
              on={prefs.admin_messages}
              onToggle={() => savePrefs({ ...prefs, admin_messages: !prefs.admin_messages })}
            />
          </CardContent>
        </Card>

        {/* Invio messaggi agli agenti */}
        {user?.role === "admin" && (
          <Card className="bg-[#18181b] border-white/5 mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base">Messaggio agli agenti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Destinatari */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">
                  Destinatari {selected.size === 0 ? "· tutti gli agenti abilitati" : `· ${selected.size} selezionati`}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    data-testid="recipient-all"
                    onClick={() => setSelected(new Set())}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      selected.size === 0
                        ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                        : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                    }`}
                  >
                    Tutti
                  </button>
                  {recipients.filter((a) => a.push_enabled).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`recipient-${a.id}`}
                      onClick={() => setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                        return next;
                      })}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        selected.has(a.id)
                          ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                          : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      }`}
                      title={`${a.devices} dispositivi iscritti`}
                    >
                      {a.name} <span className="font-mono text-[10px] text-zinc-500">{a.devices}</span>
                    </button>
                  ))}
                  {!recipients.some((a) => a.push_enabled) && (
                    <span className="text-xs text-zinc-500">Nessun agente con notifiche abilitate</span>
                  )}
                </div>
              </div>
              <Textarea
                data-testid="admin-message-input"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Es. Domani consegne anticipate alle 7:00"
                className="bg-[#0f0f11] border-white/10 text-white text-sm min-h-[80px]"
              />
              <Button
                data-testid="admin-message-send"
                onClick={() => sendAdminMessage(false)}
                disabled={sending || !msg.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-black h-9"
              >
                <Send className="w-4 h-4 mr-1.5" /> {sending ? "Invio..." : "Invia subito"}
              </Button>

              {/* Programmazione */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <input
                  type="datetime-local"
                  data-testid="admin-message-when"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                  className="h-9 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2"
                />
                <Button
                  data-testid="admin-message-schedule"
                  variant="outline"
                  onClick={() => sendAdminMessage(true)}
                  disabled={sending || !msg.trim() || !when}
                  className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 h-9"
                >
                  <Clock className="w-4 h-4 mr-1.5" /> Programma
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500">
                I messaggi programmati partono entro 15 minuti dall&apos;orario scelto.
              </p>
              <p className="text-[11px] text-zinc-500">
                Arriva solo agli agenti con le notifiche abilitate in Permessi Agenti e il tipo
                &quot;Messaggi dall&apos;admin&quot; attivo.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Storico messaggi */}
        {user?.role === "admin" && history.length > 0 && (
          <Card className="bg-[#18181b] border-white/5 mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base">Messaggi inviati e programmati</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="message-history">
              {history.map((m) => (
                <div key={m.id} className="p-3 rounded-lg border border-zinc-800 bg-zinc-800/40" data-testid={`history-${m.id}`}>
                  <div className="flex items-start gap-2">
                    <p className="text-sm text-zinc-200 break-words flex-1">{m.body}</p>
                    {m.status === "scheduled" && (
                      <button
                        type="button"
                        data-testid={`cancel-${m.id}`}
                        onClick={() => cancelScheduled(m.id)}
                        className="text-[11px] text-red-300 hover:text-red-200 shrink-0"
                      >
                        Annulla
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1">
                    {m.status === "scheduled" ? (
                      <span className="text-blue-300">
                        Programmato per {new Date(m.scheduled_at).toLocaleString("it-IT")}
                      </span>
                    ) : (
                      new Date(m.sent_at || m.created_at).toLocaleString("it-IT")
                    )}
                    {" · "}
                    {m.to_all ? "tutti gli agenti" : (m.recipient_names || []).join(", ") || `${m.recipients} agenti`}
                    {m.status !== "scheduled" && (
                      <>
                        {" · "}
                        <span className={m.sent > 0 ? "text-emerald-400" : "text-amber-400"}>
                          {m.sent} dispositivi raggiunti
                        </span>
                        {m.failed > 0 ? ` · ${m.failed} errori` : ""}
                      </>
                    )}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 text-xs text-amber-200/80 leading-relaxed">
            <p className="font-medium text-amber-200 mb-1">Suggerimenti</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Su iPhone le notifiche funzionano solo se l'app è stata aggiunta alla Home Screen (PWA).</li>
              <li>Se "Permesso browser" è <span className="font-mono">denied</span>: vai in Impostazioni iOS → Notifiche → Triolo.Store e riattiva, poi torna qui.</li>
              <li>Dopo un deploy con dominio diverso, riattiva da questa pagina.</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-zinc-400">{label}</span>
      <div className="text-right">{value}</div>
    </div>
  );
}

function PrefToggle({ label, hint, on, onToggle, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-800/40">
      <div className="min-w-0">
        <p className="text-zinc-200 text-sm font-medium">{label}</p>
        <p className="text-zinc-500 text-[11px] mt-0.5">{hint}</p>
      </div>
      <button
        type="button"
        data-testid={testId}
        onClick={onToggle}
        className={`shrink-0 w-14 h-7 rounded-full border transition-colors relative ${
          on ? "bg-amber-500/30 border-amber-500/50" : "bg-zinc-700/50 border-zinc-600"
        }`}
      >
        <span className={`absolute top-0.5 w-6 h-6 rounded-full transition-all ${
          on ? "left-7 bg-amber-400" : "left-0.5 bg-zinc-400"
        }`} />
      </button>
    </div>
  );
}
